import { useEffect, useRef } from "react";

import { FieldValues, Path, useFormContext } from "react-hook-form";

import { hasTranslationContent, TranslationFieldRequest } from "./translationContent";

export const useTranslationWriteGuard = <T extends FieldValues>() => {
  const form = useFormContext<T>();
  const state = useRef({
    writing: false,
    edited: new Set<string>(),
    expected: new Map<string, unknown>(),
  });
  const watch = form?.watch;

  useEffect(() => {
    const subscription = watch?.((_values, event) => {
      if (!event.name || state.current.writing) {
        return;
      }
      for (const [path, expected] of state.current.expected) {
        if (path === event.name || path.startsWith(`${event.name}.`)) {
          const current = form.getValues(path as Path<T>);
          if (event.type === "change" || current !== expected) {
            state.current.edited.add(path);
          }
        }
      }
    });
    return () => subscription?.unsubscribe();
  }, [form, watch]);

  return {
    getValue: (path: string): unknown => form?.getValues(path as Path<T>),
    prepare: (field: TranslationFieldRequest) => {
      const current: unknown = form?.getValues(field.path as Path<T>);
      if (
        !hasTranslationContent(field.source, field.isPlainText) ||
        hasTranslationContent(field.saved, field.isPlainText) ||
        hasTranslationContent(current, field.isPlainText) ||
        form?.getFieldState(field.path as Path<T>).isDirty ||
        state.current.edited.has(field.path)
      ) {
        return false;
      }
      state.current.expected.set(field.path, current);
      return true;
    },
    write: (path: string, value: string, setValue: (path: string, value: string) => void) => {
      const current: unknown = form?.getValues(path as Path<T>);
      if (
        state.current.edited.has(path) ||
        form?.getFieldState(path as Path<T>).isDirty ||
        (form && current !== state.current.expected.get(path))
      ) {
        state.current.edited.add(path);
        return false;
      }
      state.current.expected.set(path, value);
      state.current.writing = true;
      setValue(path, value);
      state.current.writing = false;
      return true;
    },
  };
};
